/**
 * Sysadmin Models — Catalog browser (Wave 3 / M6c criterion 1).
 *
 * Lists every Argus brand on the platform with summary counts:
 *   - Engine count (how many engines in the brand's pool)
 *   - Tenant assignment count + how many of those are enabled
 *   - Factory-default badge
 *   - Modality + author tag
 *
 * Click a row to open the brand detail page (engines, chains, etc.).
 * "+ New" button opens the create-model wizard at /sysadmin/models/new.
 *
 * Sub-nav at the top of the page lets the sysadmin pivot to the
 * other M6c surfaces (function library, trust toggles, assignments,
 * runtime monitor) without leaving the Models module.
 */

import { useEffect, useState } from 'react';
import { createFileRoute, Link } from '@tanstack/react-router';
import {
  Boxes,
  Plus,
  Search,
  ArrowRight,
  Sparkles,
  CircleDot,
  Activity as ActivityIcon,
  Users as UsersIcon,
  Library,
  ToggleRight,
  Grid3x3,
  HardDrive,
  AlertTriangle,
  Server,
  CheckCircle2,
  AlertCircle,
  HelpCircle,
} from 'lucide-react';
import { Button, buttonVariants } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '@/components/ui/table';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';
import { useArgusModels } from '@/lib/use-argus-models';
import { useTenants } from '@/lib/use-tenants';
import {
  usePreflightAssignment,
  useTenantHardware,
  type PreflightVerdict,
  type PreflightResponse,
} from '@/lib/use-sysadmin-ep1';
import { cn } from '@/lib/utils';

export const Route = createFileRoute('/sysadmin/models/')({
  component: ModelsCatalogPage,
});

const MODELS_SUB_NAV = [
  { label: 'Catalog',      path: '/sysadmin/models',                icon: Boxes },
  { label: 'Functions',    path: '/sysadmin/models/functions',      icon: Library },
  { label: 'Trust Toggles', path: '/sysadmin/models/trust-toggles', icon: ToggleRight },
  { label: 'Assignments',  path: '/sysadmin/models/assignments',    icon: Grid3x3 },
  { label: 'Runtime',      path: '/sysadmin/models/runtime',        icon: HardDrive },
];

function ModelsCatalogPage() {
  const [searchInput, setSearchInput] = useState('');
  const { data, isLoading, error } = useArgusModels();

  // EP-1 §8.2 — "Compatible on your profile?" column. The sysadmin
  // selects a tenant context; preflight runs against that tenant's
  // current hardware profile. Defaults to "no tenant selected" which
  // hides the column to avoid noise on the cross-tenant view.
  const tenantsQuery = useTenants();
  const [selectedTenantId, setSelectedTenantId] = useState<string>('');
  const tenantHw = useTenantHardware(selectedTenantId);

  // When a tenant is selected for the first time, persist to localStorage
  // so the operator's preferred tenant context survives across reloads.
  useEffect(() => {
    if (selectedTenantId) {
      try {
        window.localStorage.setItem('ep1-models-tenant-id', selectedTenantId);
      } catch {
        /* ignore */
      }
    }
  }, [selectedTenantId]);
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem('ep1-models-tenant-id');
      if (saved) setSelectedTenantId(saved);
    } catch {
      /* ignore */
    }
  }, []);

  const filtered = (data?.items ?? []).filter((m) => {
    const q = searchInput.trim().toLowerCase();
    if (!q) return true;
    return (
      m.brandId.toLowerCase().includes(q) ||
      m.displayName.toLowerCase().includes(q) ||
      m.description.toLowerCase().includes(q)
    );
  });

  const tenants = tenantsQuery.data?.tenants ?? [];
  const activeTenantHwProfile = tenantHw.data?.current?.profile ?? null;

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight flex items-center gap-2">
            <Boxes className="h-6 w-6" />
            Argus Models
          </h1>
          <p className="text-sm text-muted-foreground mt-1">
            The brand catalog the Viaanix engineering team authors. Tenants
            see only the brand name + description; engine internals stay here.
          </p>
        </div>
        <Link to="/sysadmin/models/new" className={buttonVariants()}>
          <Plus className="h-4 w-4 mr-2" />
          New model
        </Link>
      </div>

      {/* Sub-nav (catalog ↔ functions ↔ toggles ↔ assignments ↔ runtime) */}
      <ModelsSubNav active="/sysadmin/models" />

      {/* Search + tenant verdict context selector (EP-1 §8.2) */}
      <Card>
        <CardContent className="py-4 space-y-3">
          <div className="relative">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by brand, display name, or description…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              className="pl-9"
            />
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2 text-xs text-muted-foreground">
              <HardDrive className="h-3.5 w-3.5" />
              Evaluate compatibility for tenant:
            </div>
            <Select
              value={selectedTenantId || 'none'}
              onValueChange={(v) => setSelectedTenantId(v === 'none' ? '' : v)}
            >
              <SelectTrigger className="h-8 w-72 text-xs">
                <SelectValue placeholder="Select a tenant…" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none" className="text-xs italic">
                  No tenant — hide verdict column
                </SelectItem>
                {tenants.map((t) => (
                  <SelectItem key={t.id} value={t.id} className="text-xs">
                    {t.name}
                    <span className="text-muted-foreground font-mono">
                      {' '}
                      ({t.slug})
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedTenantId && activeTenantHwProfile && (
              <span className="text-xs text-muted-foreground inline-flex items-center gap-1">
                Profile:
                <Badge variant="outline" className="text-[10px]">
                  {activeTenantHwProfile.displayName}
                </Badge>
                <Badge variant="outline" className="text-[10px]">
                  {activeTenantHwProfile.vramGb} GB VRAM
                </Badge>
              </span>
            )}
            {selectedTenantId && !activeTenantHwProfile && !tenantHw.isLoading && (
              <span className="text-xs text-amber-600 inline-flex items-center gap-1">
                <AlertTriangle className="h-3.5 w-3.5" />
                Tenant has no active hardware profile assigned
              </span>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Catalog table */}
      <Card>
        <CardContent className="p-0">
          {error ? (
            <div className="p-8 text-center text-destructive text-sm">
              Failed to load catalog: {error.message}
            </div>
          ) : isLoading ? (
            <div className="p-8 text-center text-muted-foreground text-sm animate-pulse">
              Loading catalog…
            </div>
          ) : filtered.length === 0 ? (
            <EmptyState searching={searchInput.length > 0} />
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Brand</TableHead>
                  <TableHead>Modality</TableHead>
                  <TableHead className="text-right">Engines</TableHead>
                  <TableHead className="text-right">Tenants</TableHead>
                  <TableHead>Author</TableHead>
                  <TableHead>Status</TableHead>
                  {selectedTenantId && (
                    <TableHead title="EP-1 preflight verdict on the selected tenant's hardware profile">
                      Compatibility
                    </TableHead>
                  )}
                  <TableHead className="w-12"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.map((m) => (
                  <TableRow key={m.id} className="cursor-pointer hover:bg-muted/40">
                    <TableCell>
                      <Link
                        to="/sysadmin/models/$id"
                        params={{ id: m.id }}
                        className="block"
                      >
                        <div className="flex items-center gap-2">
                          <span className="font-medium">{m.displayName}</span>
                          {m.factoryDefault && (
                            <Badge variant="secondary" className="text-[10px] gap-0.5">
                              <Sparkles className="h-3 w-3" />
                              factory
                            </Badge>
                          )}
                        </div>
                        <div className="text-xs text-muted-foreground font-mono mt-0.5">
                          {m.brandId}
                        </div>
                        <div className="text-xs text-muted-foreground mt-1 line-clamp-1 max-w-md">
                          {m.description}
                        </div>
                      </Link>
                    </TableCell>
                    <TableCell>
                      <ModalityBadge modality={m.modality} />
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      {m.engineCount}
                    </TableCell>
                    <TableCell className="text-right tabular-nums">
                      <span className="font-medium">{m.enabledAssignmentCount}</span>
                      <span className="text-muted-foreground">
                        {' '}/ {m.assignmentCount}
                      </span>
                    </TableCell>
                    <TableCell>
                      <Badge
                        variant="outline"
                        className={cn(
                          'text-[10px]',
                          m.author === 'factory' ? 'border-blue-300' : 'border-purple-300',
                        )}
                      >
                        {m.author}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <BrandStatusPill brand={m} />
                    </TableCell>
                    {selectedTenantId && (
                      <TableCell>
                        <CompatibilityCell
                          tenantId={selectedTenantId}
                          brandId={m.id}
                          hasProfile={activeTenantHwProfile !== null}
                        />
                      </TableCell>
                    )}
                    <TableCell>
                      <Link
                        to="/sysadmin/models/$id"
                        params={{ id: m.id }}
                        className="text-muted-foreground hover:text-foreground"
                      >
                        <ArrowRight className="h-4 w-4" />
                      </Link>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Footer summary */}
      {!isLoading && data && (
        <div className="flex items-center justify-between text-sm text-muted-foreground">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1.5">
              <ActivityIcon className="h-3.5 w-3.5" />
              {data.items.filter((m) => m.isActive).length} active
            </span>
            <span className="flex items-center gap-1.5">
              <UsersIcon className="h-3.5 w-3.5" />
              {data.items.reduce((sum, m) => sum + m.enabledAssignmentCount, 0)} active assignments
            </span>
          </div>
          {searchInput && (
            <span>
              {filtered.length} of {data.items.length} brands match search
            </span>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Helpers ──────────────────────────────────────────────────────────────

export function ModelsSubNav({ active }: { active: string }) {
  return (
    <nav className="flex items-center gap-1 overflow-x-auto whitespace-nowrap border-b">
      {MODELS_SUB_NAV.map((tab) => {
        const Icon = tab.icon;
        const isActive = active === tab.path;
        return (
          <Link
            key={tab.path}
            to={tab.path}
            className={cn(
              'flex items-center gap-2 px-3 py-2 text-sm font-medium border-b-2 -mb-[1px] transition-colors',
              isActive
                ? 'border-primary text-foreground'
                : 'border-transparent text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-4 w-4" />
            {tab.label}
          </Link>
        );
      })}
    </nav>
  );
}

function ModalityBadge({ modality }: { modality: string }) {
  const tone =
    modality === 'chat'
      ? 'border-blue-300 text-blue-700'
      : modality === 'vision'
        ? 'border-purple-300 text-purple-700'
        : modality === 'code'
          ? 'border-emerald-300 text-emerald-700'
          : modality === 'reason'
            ? 'border-amber-300 text-amber-700'
            : modality === 'long'
              ? 'border-cyan-300 text-cyan-700'
              : modality === 'image-gen'
                ? 'border-pink-300 text-pink-700'
                : 'border-muted text-muted-foreground';
  return (
    <Badge variant="outline" className={cn('text-[10px]', tone)}>
      {modality}
    </Badge>
  );
}

/**
 * Renders the right-most "Status" pill in the catalog table.
 *
 * Precedence (most-specific first):
 *   1. purpose='infra'        → "Infrastructure" (system brand, hidden
 *                                from tenant picker; sysadmin-only)
 *   2. !isActive              → "Retired" or "DEFERRED" (when a known
 *                                deferral reason applies, e.g. argus_lite
 *                                Gemma 4 E2B segfault on Strix Halo per
 *                                project_wave_3_m6d_complete.md)
 *   3. engineCount===0        → "No active engine" warning
 *   4. isActive               → "active"
 */
function BrandStatusPill({ brand }: { brand: { isActive: boolean; purpose: 'tenant' | 'infra'; engineCount: number; brandId: string } }) {
  // (1) Infrastructure brand (purpose='infra'). Render whether active or not
  // so sysadmin can always see these are system-only.
  if (brand.purpose === 'infra') {
    return (
      <Badge
        variant="outline"
        className="text-blue-700 border-blue-300 gap-1 text-[10px]"
        title="System infrastructure brand — used by the RAG pipeline / classifier / image-gen runtime. Not selectable by tenants."
      >
        <Server className="h-3 w-3" />
        Infrastructure
      </Badge>
    );
  }

  // (2) Retired — with a special "DEFERRED" pill for known deferrals
  // so the sysadmin sees WHY the brand is retired.
  if (!brand.isActive) {
    const isDeferredArgusLite = brand.brandId === 'argus_lite';
    const isDeferredArgusDraw = brand.brandId === 'argus_draw';
    if (isDeferredArgusLite) {
      return (
        <Badge
          variant="outline"
          className="text-amber-700 border-amber-300 gap-1 text-[10px]"
          title="Gemma 4 E2B engine segfaults on Strix Halo gfx1100 b8987 — deferred indefinitely per project_wave_3_m6d_complete.md. Add an alternate engine (e.g. gpt-oss-20b shared with ArgusFast) via Engine pool to re-enable."
        >
          <AlertTriangle className="h-3 w-3" />
          DEFERRED — Gemma 4 not deployed
        </Badge>
      );
    }
    if (isDeferredArgusDraw) {
      return (
        <Badge
          variant="outline"
          className="text-amber-700 border-amber-300 gap-1 text-[10px]"
          title="argus_draw FLUX image-gen runtime not deployed (visibleAtGateway=false). Deploy the runtime and re-enable via Engine pool."
        >
          <AlertTriangle className="h-3 w-3" />
          DEFERRED — image-gen runtime not deployed
        </Badge>
      );
    }
    return (
      <Badge
        variant="outline"
        className="text-muted-foreground gap-1 text-[10px]"
      >
        retired
      </Badge>
    );
  }

  // (3) Active brand but no active engine — a UI promise the dispatch
  // cannot keep. Surface as a warning so the sysadmin notices BEFORE
  // a tenant clicks through and times out at the gateway.
  if (brand.engineCount === 0) {
    return (
      <Badge
        variant="outline"
        className="text-amber-700 border-amber-300 gap-1 text-[10px]"
        title="Brand has no active engine. Dispatch will fail. Add or activate an engine via the Engine pool table."
      >
        <AlertTriangle className="h-3 w-3" />
        no active engine
      </Badge>
    );
  }

  // (4) Healthy active brand.
  return (
    <Badge
      variant="outline"
      className="text-emerald-700 border-emerald-300 gap-1 text-[10px]"
    >
      <CircleDot className="h-3 w-3" />
      active
    </Badge>
  );
}

function EmptyState({ searching }: { searching: boolean }) {
  if (searching) {
    return (
      <div className="p-8 text-center text-sm text-muted-foreground">
        No brands match your search.
      </div>
    );
  }
  return (
    <div className="p-8 text-center space-y-3">
      <Boxes className="h-10 w-10 text-muted-foreground mx-auto" />
      <h3 className="text-base font-semibold">No Argus models yet</h3>
      <p className="text-sm text-muted-foreground max-w-md mx-auto">
        The catalog is empty. Run the M6a seed (or create one manually via
        the wizard) to populate the brand catalog.
      </p>
      <Link to="/sysadmin/models/new" className={buttonVariants()}>
        <Plus className="h-4 w-4 mr-2" />
        Create your first model
      </Link>
    </div>
  );
}

// ─── EP-1 §8.2 — Compatibility cell + verdict helpers ───────────────────

/**
 * Renders a single brand's preflight verdict against the selected
 * tenant's hardware profile. Calls `POST /preflight-assignment` on
 * mount + caches the result for the row's lifetime. Click → opens a
 * detail popover with reasoning + baseline detail.
 */
export function CompatibilityCell({
  tenantId,
  brandId,
  hasProfile,
}: {
  tenantId: string;
  brandId: string;
  hasProfile: boolean;
}) {
  const preflight = usePreflightAssignment(tenantId);
  const [result, setResult] = useState<PreflightResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [expanded, setExpanded] = useState(false);

  // Auto-evaluate on mount + when inputs change. We use the mutation
  // hook because the endpoint is a POST that takes a body.
  useEffect(() => {
    let cancelled = false;
    if (!hasProfile) {
      setResult(null);
      return;
    }
    (async () => {
      try {
        const res = await preflight.mutateAsync({ brandId });
        if (!cancelled) {
          setResult(res);
          setErrorMsg(null);
        }
      } catch (e) {
        if (!cancelled) {
          setErrorMsg(e instanceof Error ? e.message : 'Unknown error');
          setResult(null);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId, brandId, hasProfile]);

  if (!hasProfile) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 text-muted-foreground"
        title="Selected tenant has no active hardware profile"
      >
        <HelpCircle className="h-3 w-3" />
        N/A
      </Badge>
    );
  }
  if (preflight.isPending && !result) {
    return (
      <Badge variant="outline" className="text-[10px] gap-1 text-muted-foreground animate-pulse">
        evaluating…
      </Badge>
    );
  }
  if (errorMsg && !result) {
    return (
      <Badge
        variant="outline"
        className="text-[10px] gap-1 text-amber-700 border-amber-300"
        title={errorMsg}
      >
        <AlertTriangle className="h-3 w-3" />
        error
      </Badge>
    );
  }
  if (!result) return null;

  return (
    <div className="space-y-1">
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        className="inline-flex items-center"
        title="Click for verdict details"
      >
        <ModelsVerdictBadge verdict={result.verdict} />
      </button>
      {expanded && (
        <div className="text-[11px] text-muted-foreground border-l-2 border-muted pl-2 max-w-xs">
          <div className="font-medium text-foreground tabular-nums">
            p95 {result.projectedFirstTokenP95Ms ?? '—'} ms / budget{' '}
            {result.measuredHelperBudgetMs} ms
          </div>
          {result.reasoning?.length > 0 ? (
            <ul className="list-disc ml-3 mt-0.5">
              {result.reasoning.map((r, i) => (
                <li key={i}>{r}</li>
              ))}
            </ul>
          ) : (
            <div className="mt-0.5">All checks passed.</div>
          )}
          {result.baseline ? (
            <div className="text-[10px] text-muted-foreground mt-1">
              baseline source: {result.baseline.source}
              {' · '}
              measured {new Date(result.baseline.measuredAt).toLocaleDateString()}
            </div>
          ) : (
            <div className="text-[10px] text-amber-600 mt-1">
              No baseline measured for this brand on this profile
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/**
 * Single verdict badge with semantic tone. Mirrors the same primitive
 * exported from `tenants.$tenantId.hardware.tsx` but kept local to the
 * Models page so the cross-file import graph stays shallow.
 */
function ModelsVerdictBadge({ verdict }: { verdict: PreflightVerdict }) {
  if (verdict === 'green') {
    return (
      <Badge
        className="text-[10px] gap-1 bg-emerald-100 text-emerald-700 border-emerald-300 dark:bg-emerald-900/30 dark:text-emerald-400 cursor-pointer"
        title="GREEN — p95 < budget × 0.7 — comfortable headroom"
      >
        <CheckCircle2 className="h-3 w-3" />
        GREEN
      </Badge>
    );
  }
  if (verdict === 'yellow') {
    return (
      <Badge
        className="text-[10px] gap-1 bg-amber-100 text-amber-700 border-amber-300 dark:bg-amber-900/30 dark:text-amber-400 cursor-pointer"
        title="YELLOW — p95 in 70-150% of budget — at risk under load"
      >
        <AlertCircle className="h-3 w-3" />
        YELLOW
      </Badge>
    );
  }
  return (
    <Badge
      className="text-[10px] gap-1 bg-red-100 text-red-700 border-red-300 dark:bg-red-900/30 dark:text-red-400 cursor-pointer"
      title="RED — p95 > budget × 1.5 — will time out without risk-accept"
    >
      <AlertTriangle className="h-3 w-3" />
      RED
    </Badge>
  );
}

/**
 * Pure verdict-tier classifier — exported for unit tests so the
 * threshold logic is verifiable without spinning up the full preflight
 * pipeline. Mirrors EP-1 §7.2 step 3.
 */
export function classifyVerdictTier(
  p95Ms: number | null,
  budgetMs: number,
  thresholdGreen = 0.7,
  thresholdRed = 1.5,
): PreflightVerdict | 'na' {
  if (p95Ms === null || p95Ms === undefined) return 'na';
  if (budgetMs <= 0) return 'na';
  const ratio = p95Ms / budgetMs;
  if (ratio < thresholdGreen) return 'green';
  if (ratio < thresholdRed) return 'yellow';
  return 'red';
}

// Silence the unused-import warning for Button — it's reserved for the
// expand-detail future enhancement and we keep the import to avoid
// churning lint state when that lands.
void Button;
